import styled, { css } from 'styled-components';
import { baseVariables, getStyleClassPerTheme, toRem } from '@marriott/mi-ui-library';

const borderRadius = toRem(14);
const mobileScrimGradientHeight = toRem(172);
const darkScrimXsTop = 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.54) 100%)';
const darkScrimXsBottom = 'linear-gradient(0deg, rgba(0, 0, 0, 0.54) 0%, rgba(0, 0, 0, 0.54) 100%)';
const darkScrimMd = 'linear-gradient(270deg, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 0.52) 50%, rgba(0, 0, 0, 0.54) 100%)';
const darkScrimLg =
  'linear-gradient(270deg, rgba(0, 0, 0, 0) 26.58%, rgba(0, 0, 0, 0.52) 48.95%, rgba(0, 0, 0, 0.54) 100%)';

const lightScrimXsTop = 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.54) 100%)';
const lightScrimXsBottom = 'linear-gradient(0deg, rgba(255, 255, 255, 0.54) 0%, rgba(255, 255, 255, 0.54) 100%)';
const lightScrimMd =
  'linear-gradient(270deg, rgba(255, 255, 255, 0) 25%, rgba(255, 255, 255, 0.54) 50%, rgba(255, 255, 255, 0.54) 100%)';
const lightScrimLg =
  'linear-gradient(270deg, rgba(255, 255, 255, 0) 26.58%, rgba(255, 255, 255, 0.54) 48.95%, rgba(255, 255, 255, 0.54) 100%)';

export const StyledPhotoCaption = styled.div`
  gap: ${toRem(4)};
  background: rgba(0, 0, 0, 0.54);
  position: absolute;
  top: ${toRem(16)};
  left: ${toRem(16)};
  border-radius: ${toRem(4)};
  backdrop-filter: blur(${toRem(2)});
  z-index: 2;
  display: none;

  .icon {
    color: ${baseVariables.color['base20']};
  }

  @media ${baseVariables.mediaQuery.md} {
    display: flex;
    left: auto;
    right: ${toRem(16)};
  }
`;

export const StyledPhotoCaptionText = styled.span`
  color: ${baseVariables.color['base20']} !important; // !important added to override color from style class t-font-xs
  font-weight: 700;
`;

/** Matches MTextBlockSimple layout (flex column + alignment). */
export const LayeredFeatureTextBlockWrapper = styled.div<{ $centered?: boolean }>`
  text-align: ${({ $centered }) => ($centered ? 'center' : 'left')};
`;

export const StyledMerchandisingLayeredFeature = styled.div<{ $theme: string | undefined; $type: 'cf' | 'dca' }>(
  ({ $theme }) => {
    const themeClass = getStyleClassPerTheme($theme);
    const isStandard = themeClass === 'standard';
    const gradientXsTop = isStandard ? lightScrimXsTop : darkScrimXsTop;
    const gradientXsBottom = isStandard ? lightScrimXsBottom : darkScrimXsBottom;
    const gradientMd = isStandard ? lightScrimMd : darkScrimMd;
    const gradientLg = isStandard ? lightScrimLg : darkScrimLg;

    return css`
      position: relative;
      overflow: hidden;
      aspect-ratio: 1;
      border-radius: ${toRem(14)};

      @media ${baseVariables.mediaQuery.md} {
        aspect-ratio: 2;
      }

      @media ${baseVariables.mediaQuery.xl} {
        aspect-ratio: 3;
      }

      .lyrd_ft {
        &__cta-cnt {
          gap: ${toRem(8)};
        }

        &__cnt-wrapper {
          z-index: 1;

          &.math-details-padding-sm {
            padding-top: ${toRem(12)};
            padding-bottom: ${toRem(12)};

            @media ${baseVariables.mediaQuery.md} {
              padding-top: 0;
              padding-bottom: 0;
            }
          }

          .logo-icon {
            aspect-ratio: 3 / 2;
            height: ${toRem(60)};
            max-width: ${toRem(90)};
            justify-content: left;
            /* WEB-202411: keep LogoContainer's fixed 135x90 inner span/svg within this box on phone & iPad */
            overflow: hidden;

            span:not(.sr-only) {
              width: 100%;
              height: 100%;
            }

            svg {
              max-width: 100%;
              max-height: 100%;
            }

            @media ${baseVariables.mediaQuery.lg} {
              height: ${toRem(90)};
              max-width: ${toRem(135)};
            }
          }

          .logo-icon::before {
            font-size: ${toRem(50)};
            line-height: ${toRem(50)};

            @media ${baseVariables.mediaQuery.lg} {
              font-size: ${toRem(80)};
              line-height: ${toRem(80)};
            }
          }

          .credit-card-image {
            height: 100%;
            max-height: ${toRem(118)};
            max-width: 100%;
            width: auto;
          }
          .credit-card-image-math {
            height: 100%;
            max-height: ${toRem(42)};
            @media ${baseVariables.mediaQuery.md} {
              max-height: ${toRem(63.33)};
            }
            max-width: 100%;
            width: auto;
          }

          .logo-image {
            height: 100%;
            max-height: ${toRem(60)};
            max-width: 100%;
            width: auto;

            @media ${baseVariables.mediaQuery.lg} {
              max-height: ${toRem(90)};
            }
          }
        }

        &__feature-img {
          border-radius: ${borderRadius};

          &-wrapper {
            height: 100%;
            width: 100%;
          }
        }

        &__gradient {
          position: relative;

          &:after {
            height: 100%;
            z-index: 0;
            display: block;
            width: 100%;
            position: absolute;
            content: '';
            bottom: 0;
            left: 0;
            right: 0;
            border-radius: ${borderRadius};
            background-image: ${gradientXsTop}, ${gradientXsBottom};
            background-repeat: no-repeat;
            background-size: 100% ${mobileScrimGradientHeight}, 100% calc(100% - ${mobileScrimGradientHeight});
            background-position: top left, bottom left;

            @media ${baseVariables.mediaQuery.md} {
              background: ${gradientMd};
            }

            @media ${baseVariables.mediaQuery.lg} {
              background: ${gradientLg};
            }
          }
        }

        &__cnt-sec {
          position: absolute;
          z-index: 1;
          top: 0;
          bottom: 0;
          right: ${toRem(8)};
          left: ${toRem(8)};
          align-items: flex-end;

          &:before {
            display: none;
          }

          @media ${baseVariables.mediaQuery.md} {
            align-items: center;
          }
        }

        &__cnt-ctr {
          position: relative;
          z-index: 1;
        }

        &__txt-cnt {
          padding-top: ${toRem(16)};
          padding-bottom: ${toRem(16)};

          &.cb-banner__math {
            padding-top: ${toRem(8)};
            padding-bottom: ${toRem(4)};

            @media ${baseVariables.mediaQuery.md} {
              padding-top: ${toRem(16)};
              padding-bottom: ${toRem(8)};
            }
          }
        }

        &__credit-card-img {
          width: ${toRem(150)};
          height: auto;

          &--sm {
            width: ${toRem(66)};

            @media ${baseVariables.mediaQuery.md} {
              width: ${toRem(100)};
            }
          }
        }
      }
    `;
  }
);
